import React from 'react';
import { Box, Skeleton, Typography, alpha } from '@mui/material';
import { ForwardToInboxOutlined, MailOutline } from '@mui/icons-material';
import type { UpcomingEmails } from './useUpcomingEmails';

interface Props {
  upcoming: UpcomingEmails;
  /** When the emails leave, e.g. "as soon as you apply". */
  when?: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Before staff confirm, one plain sentence: which email goes, to which
 * address, and that it can't be unsent. Shows nothing when no email is
 * switched on for the action.
 */
const UpcomingEmailNotice: React.FC<Props> = ({ upcoming, when = 'as soon as you confirm' }) => {
  const { loading, error, groups } = upcoming;
  if (loading) return <Skeleton variant="rounded" height={52} sx={{ borderRadius: 2 }} />;
  if (error) return <Typography variant="caption" color="warning.dark">Couldn’t check the email: {error}</Typography>;
  if (groups.length === 0) return null;

  const emailed = groups.reduce((n, g) => n + g.emailed, 0);
  const missing = groups.reduce((n, g) => n + g.missing, 0);
  const single = emailed + missing === 1;

  let text: React.ReactNode;
  if (single && emailed) {
    const g = groups.find(x => x.emailed)!;
    text = <>The {g.noun} email will be sent to <b>{g.to.join(', ')}</b> {when}. It can’t be unsent.</>;
  } else if (single) {
    text = 'No email will be sent: this application has no email address.';
  } else if (emailed) {
    const parts = groups.filter(g => g.emailed).map(g => plural(g.emailed, `${g.noun} email`, `${g.noun} emails`));
    text = `${parts.join(' and ')} will be sent ${when}.`
      + (missing ? ` ${plural(missing, 'application has', 'applications have')} no email address, so ${missing === 1 ? 'it gets' : 'they get'} none.` : '')
      + ' Emails can’t be unsent.';
  } else {
    text = 'No emails will be sent: none of these applications has an email address.';
  }

  return (
    <Box sx={(t) => ({
      display: 'flex', gap: 1.25, alignItems: 'flex-start', px: 1.5, py: 1.25, borderRadius: 2,
      bgcolor: emailed ? alpha(t.palette.primary.main, 0.06) : alpha(t.palette.text.primary, 0.03),
      border: `1px solid ${emailed ? alpha(t.palette.primary.main, 0.2) : t.palette.divider}`,
    })}>
      {emailed
        ? <ForwardToInboxOutlined fontSize="small" sx={{ color: 'primary.main', mt: '2px' }} />
        : <MailOutline fontSize="small" sx={{ color: 'text.secondary', mt: '2px' }} />}
      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', '& b': { color: 'text.primary', fontWeight: 600 } }}>
        {text}
      </Typography>
    </Box>
  );
};

export default UpcomingEmailNotice;
